import AuthService from "@/core/services/Auth.service";
import {
  ensureStudioSession,
  getLastSuccessfulBootstrapMs,
  type BootstrapSessionResult,
} from "@/infrastructure/services/auth/bootstrapSession";

/** Slightly under the 15m studio access-token TTL so tokens refresh before expiry. */
export const SESSION_KEEP_ALIVE_INTERVAL_MS = 14 * 60 * 1000;

const AUTH0_DEAD_CODES = new Set(["AUTH0_TOKEN_ERROR", "AUTH0_TOKEN_UNAVAILABLE"]);

export type SessionKeepAliveHandlers = {
  /** Called only when Auth0 itself can no longer renew (true session end). */
  onAuth0Expired?: () => void;
};

/**
 * Forces Auth0 token renew, then re-fetches studio session (user + tokens)
 * and persists them in session storage.
 */
export async function refreshSessionLifecycle(): Promise<BootstrapSessionResult> {
  try {
    const token = await AuthService.getInstance().getUserAccessToken({ cacheMode: "off" });
    if (!token) {
      return {
        ok: false,
        code: "AUTH0_TOKEN_UNAVAILABLE",
        message: "Unable to retrieve authentication token",
      };
    }
  } catch {
    return {
      ok: false,
      code: "AUTH0_TOKEN_ERROR",
      message: "Your authentication session has expired. Please sign in again.",
    };
  }

  return ensureStudioSession({ force: true });
}

/**
 * Periodically refreshes Auth0 + studio session while the tab is active.
 * Also refreshes when the tab becomes visible again if the interval elapsed.
 * Returns a dispose function.
 */
export function startSessionKeepAlive(
  handlers: SessionKeepAliveHandlers = {},
  intervalMs: number = SESSION_KEEP_ALIVE_INTERVAL_MS,
): () => void {
  let disposed = false;

  const run = async (): Promise<void> => {
    if (disposed) {
      return;
    }
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }

    const result = await refreshSessionLifecycle();
    if (disposed) {
      return;
    }

    if (!result.ok && result.code && AUTH0_DEAD_CODES.has(result.code)) {
      handlers.onAuth0Expired?.();
    }
  };

  const timer = globalThis.setInterval(() => {
    void run();
  }, intervalMs);

  const onVisibilityChange = (): void => {
    if (typeof document === "undefined" || document.visibilityState !== "visible") {
      return;
    }
    if (Date.now() - getLastSuccessfulBootstrapMs() >= intervalMs) {
      void run();
    }
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  return () => {
    disposed = true;
    globalThis.clearInterval(timer);
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  };
}
